/**
 * User Actions Menu
 *
 * The per-user "⋯" menu shared by Admin → Users and Company → Members. Each
 * item renders only when its callback is given. From a company context the
 * destructive item is "Remove from company" (membership only) and replaces
 * account Delete outright, so the two are never offered side by side.
 *
 * Clicks are contained: the menu sits inside clickable table rows, and a
 * click on it must never also open the row.
 */

import React from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@lognai/synapse-design-system-v2';
import {
  Edit,
  Shield,
  Key,
  Lock,
  UserCog,
  UserMinus,
  Trash2,
  MoreHorizontal,
} from '@lognai/synapse-design-system-v2/icons';
import type { User } from '@lognai/core-platform';

export interface UserActionCallbacks {
  onEditUser?: (user: User) => void;
  onResetPassword?: (user: User) => void;
  onManageRoles?: (user: User) => void;
  onManageAccess?: (user: User) => void;
  onActivateUser?: (user: User) => void;
  onSuspendUser?: (user: User) => void;
  onDeleteUser?: (user: User) => void;
  /** Rendered instead of Delete when given. */
  onRemoveFromCompany?: (user: User) => void;
}

export interface UserActionsMenuProps extends UserActionCallbacks {
  user: User;
}

export const UserActionsMenu: React.FC<UserActionsMenuProps> = ({
  user,
  onEditUser,
  onResetPassword,
  onManageRoles,
  onManageAccess,
  onActivateUser,
  onSuspendUser,
  onDeleteUser,
  onRemoveFromCompany,
}) => (
  <div onClick={(e) => e.stopPropagation()}>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={`Actions for ${user.fullName || user.email}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onEditUser && (
          <DropdownMenuItem onClick={() => onEditUser(user)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit User
          </DropdownMenuItem>
        )}
        {onResetPassword && (
          <DropdownMenuItem onClick={() => onResetPassword(user)}>
            <Lock className="mr-2 h-4 w-4" />
            Password link
          </DropdownMenuItem>
        )}
        {onManageRoles && (
          <DropdownMenuItem onClick={() => onManageRoles(user)}>
            <Shield className="mr-2 h-4 w-4" />
            Manage Roles
          </DropdownMenuItem>
        )}
        {onManageAccess && (
          <DropdownMenuItem onClick={() => onManageAccess(user)}>
            <Key className="mr-2 h-4 w-4" />
            Manage Access
          </DropdownMenuItem>
        )}
        {onActivateUser && user.status !== 'active' && (
          <DropdownMenuItem onClick={() => onActivateUser(user)}>
            <UserCog className="mr-2 h-4 w-4" />
            Activate
          </DropdownMenuItem>
        )}
        {onSuspendUser && user.status !== 'suspended' && (
          <DropdownMenuItem onClick={() => onSuspendUser(user)}>
            <UserCog className="mr-2 h-4 w-4" />
            Suspend
          </DropdownMenuItem>
        )}
        {onRemoveFromCompany ? (
          <DropdownMenuItem onClick={() => onRemoveFromCompany(user)} className="text-destructive">
            <UserMinus className="mr-2 h-4 w-4" />
            Remove from company
          </DropdownMenuItem>
        ) : (
          onDeleteUser && (
            <DropdownMenuItem onClick={() => onDeleteUser(user)} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete User
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);
